import React, { FC, useEffect } from 'react';
import { AxiosPromise } from 'axios';
import classNames from 'classnames';
import { useFormikContext } from 'formik';
import styles from './styles.module.css';

import { FormInput } from 'Shared/components';
import { CheckmarkIcon, NoIcon, Button } from '@clearcover/atlas';
import {
  AdasLabelsType,
  AdasStandardGroupType,
  AdasStandardGroupsType,
} from '@clearcover/core-app-types';
import { AddVehicleFormValues } from './VehicleForm';

const isAdasDataComplete = (adas: AdasStandardGroupsType) =>
  [adas.active, adas.drivingFeedback, adas.parkingFeedback].every(
    group => group.equipped !== 'unknown'
  );

export const AdvancedDriverAssistanceSystems: FC<{
  type: 'create' | 'update';
  adasLabels: Array<AdasLabelsType>;
  adasReportFunc: {
    getAdasReportData: () => AxiosPromise<AdasStandardGroupsType>;
    loading: boolean;
  };
}> = ({ type, adasLabels, adasReportFunc: { getAdasReportData, loading } }) => {
  const { values, setFieldValue } = useFormikContext<AddVehicleFormValues>();

  useEffect(() => {
    setFieldValue('adasReportRan', false);
  }, [values.year, values.vin, values.make, values.model, setFieldValue]);

  useEffect(() => {
    setFieldValue('adasReportRan', isAdasDataComplete(values.adas));
  }, [values.adas, setFieldValue]);

  return (
    <section className={styles.section} data-testid="vehicle-advance-systems">
      <h1>Advanced Driver-Assistance Systems</h1>
      <p>
        Customers in Oregon are required to verify any optional advanced driver
        assistance systems found on their vehicle. We will need to verify this
        information before adding the vehicle.
      </p>
      <Button
        className={styles.runReportButton}
        onClick={() => {
          getAdasReportData().then(({ data: adasReportData }) => {
            const updateAdasGroup = (
              group:
                | 'active'
                | 'drivingFeedback'
                | 'parkingFeedback'
                | 'passive'
            ): AdasStandardGroupType => {
              const isoDataIsKnown =
                values.adas[group] && values.adas[group].equipped !== 'unknown';
              const reportDataisKnown =
                adasReportData[group] &&
                adasReportData[group].equipped !== 'unknown';
              if (!reportDataisKnown && !isoDataIsKnown)
                return {
                  equipped: 'no',
                  source: 'userReported',
                };

              if (reportDataisKnown)
                return {
                  equipped: adasReportData[group].equipped,
                  source: adasReportData[group].source,
                };

              return values.adas[group];
            };

            const newAdasData = {
              active: updateAdasGroup('active'),
              drivingFeedback: updateAdasGroup('drivingFeedback'),
              parkingFeedback: updateAdasGroup('parkingFeedback'),
              passive: updateAdasGroup('passive'),
            };
            setFieldValue('adas', newAdasData);
            setFieldValue('adasReportRan', true);
          });
        }}
        isSubmitting={loading}
        disabled={
          type === 'update' ||
          values.adasReportRan ||
          !!!values.avgMiles ||
          !!!values.ownershipStatus
        }
      >
        {values.adasReportRan
          ? 'Report ran successfully'
          : 'Verify ADAS Report Data'}
      </Button>
      {(!!!values.avgMiles || !!!values.ownershipStatus) && (
        <div className={styles.adasWarningMessage}>
          {!!!values.avgMiles && 'Avg Miles'}
          {!!!values.avgMiles && !!!values.ownershipStatus && ' and '}
          {!!!values.ownershipStatus && 'Ownership status'} must be filled out
          before verifying adas report data.
        </div>
      )}
      {adasLabels.map(({ label, names, id }) => {
        return (
          <section className={styles.assistanceType} key={id}>
            <h3 className={styles.assistanceLabel}>{label}</h3>
            <ul className={styles.assistanceTypeList}>
              {names.map(n => (
                <li key={n.id}> {n.label} </li>
              ))}
            </ul>

            <div
              className={classNames(styles.installed, {
                [styles.notInstalled]: values.adas[id]?.equipped === 'no',
              })}
            >
              {values.adas[id]?.equipped === 'yes' &&
                values.adas[id]?.source !== 'userReported' && (
                  <>
                    <CheckmarkIcon width={12} height={12} /> One or more
                    installed
                  </>
                )}
              {values.adas[id]?.equipped === 'no' &&
                values.adas[id]?.source !== 'userReported' && (
                  <>
                    <NoIcon width={12} height={12} /> This is not installed
                  </>
                )}
              {values.adas[id]?.source === 'userReported' && (
                <>
                  <FormInput
                    name={'isIncluded' + id}
                    value={values.adas[id].equipped}
                    onChange={(v: string) => {
                      setFieldValue('adas', {
                        ...values.adas,
                        [id]: {
                          equipped: v,
                          source: 'userReported',
                        },
                      });
                    }}
                    disabled={type === 'update'}
                    variant={'radioButtonGroup'}
                    options={{ yes: 'Yes', no: 'no' }}
                    label={'Are any of these features installed?'}
                  />
                </>
              )}
            </div>
          </section>
        );
      })}
    </section>
  );
};
